/**
 * Category Tools - AI-facing interface
 *
 * Provides 3 methods for AI to interact with the tool category system:
 * 1. getCategoryInfo - Explore category structure
 * 2. searchTools - Find tools by query (returns full definitions with parameters)
 * 3. executeTool - Execute tools by path
 */

/**
 * Get category information
 * Allows AI to explore the category tree
 *
 * @param {ToolCategory} rootCategory - Root category
 * @param {Object} ctx - Request context
 * @param {Object} args - Arguments
 * @param {string} [args.categoryPath=''] - Category path to explore (empty for root)
 * @returns {Promise<Object>} Category information
 */
export async function getCategoryInfo(rootCategory, ctx, args) {
  const { categoryPath = '' } = args

  try {
    // Get category (empty path returns rootCategory itself)
    const category = categoryPath
      ? rootCategory.getCategoryByPath(categoryPath)
      : rootCategory

    if (!category) {
      return {
        success: false,
        error: `Category '${categoryPath}' not found`,
      }
    }

    // Unified return format for both root and sub-categories
    return {
      success: true,
      info: {
        id: category.id,
        description: category.description,
        path: category.getPath(),
      },
      subCategories: Array.from(category.subCategories.values()).map((c) => ({
        id: c.id,
        description: c.description,
      })),
      tools: Array.from(category.tools.entries()).map(([name, tool]) => tool.definition),
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
    }
  }
}

/**
 * Search for tools
 * Supports both global search and scoped search within a category
 * Supports semantic search (Phase 2) with automatic fallback to keyword search
 *
 * @param {ToolCategory} rootCategory - Root category
 * @param {Object} ctx - Request context
 * @param {Object} args - Arguments
 * @param {string} args.query - Search query
 * @param {string} [args.categoryPath] - Optional category path to scope search
 * @param {boolean} [args.semantic=true] - Enable semantic search (default: true)
 * @param {number} [args.minSimilarity=0.3] - Minimum similarity threshold (default: 0.3)
 * @param {number} [args.limit=5] - Maximum number of results (default: 5)
 * @returns {Promise<Object>} Search results (simplified: name, path, description only)
 */
export async function searchTools(rootCategory, ctx, args) {
  const { query, categoryPath, semantic = true, minSimilarity = 0.3, limit = 5 } = args

  if (!query) {
    return {
      success: false,
      error: 'query is required',
    }
  }

  try {
    let results

    const searchOptions = { semantic, minSimilarity }

    if (categoryPath) {
      // Scoped search: search within specific category
      const category = rootCategory.getCategoryByPath(categoryPath)
      if (!category) {
        return {
          success: false,
          error: `Category '${categoryPath}' not found`,
          results: [],
          count: 0,
        }
      }
      results = await category.searchRecursively(query, searchOptions)
    } else {
      // Global search: search across all categories
      results = await rootCategory.searchRecursively(query, searchOptions)
    }

    const limitedResults = results.slice(0, limit).map(r => ({
      name: r.name,
      path: r.path,
      description: r.description,
      parameters: r.parameters,
      score: r.score,
    }))

    return {
      success: true,
      results: limitedResults,
      count: limitedResults.length,
      totalMatches: results.length,
      searchMode: semantic ? 'hybrid' : 'keyword',
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
      results: [],
      count: 0,
    }
  }
}

/**
 * Execute a tool by its full path
 *
 * @param {ToolCategory} rootCategory - Root category
 * @param {Object} ctx - Request context
 * @param {Object} args - Arguments
 * @param {string} args.toolPath - Full tool path like "notes/saveNote"
 * @param {Object} args.toolArgs - Arguments to pass to the tool
 * @returns {Promise<Object>} Execution result
 */
export async function executeTool(rootCategory, ctx, args) {
  const { toolPath, toolArgs } = args

  if (!toolPath) {
    return {
      success: false,
      error: 'toolPath is required',
    }
  }

  try {
    const result = await rootCategory.executeByPath(toolPath, ctx, toolArgs)
    return {
      success: true,
      result,
    }
  } catch (error) {
    console.error(`[executeTool] ${toolPath} failed:`, error.message)
    return {
      success: false,
      error: 'Tool execution failed. Verify the path format (category/tool) and arguments.',
    }
  }
}

/**
 * Create category tools bound to a specific root category
 * @param {ToolCategory} rootCategory - Root category
 * @returns {Object} Category tools
 */
export function createCategoryTools(rootCategory) {
  return {
    getCategoryInfo: (ctx, args) => getCategoryInfo(rootCategory, ctx, args),
    searchTools: (ctx, args) => searchTools(rootCategory, ctx, args),
    executeTool: (ctx, args) => executeTool(rootCategory, ctx, args),
  }
}

/**
 * Get top-level category summary for system prompt
 * @param {ToolCategory} rootCategory - Root category instance
 * @returns {Array} Top-level categories summary
 */
export function getTopLevelCategorySummary(rootCategory) {
  return Array.from(rootCategory.subCategories.values()).map((c) => ({
    id: c.id,
    description: c.description,
    path: c.getPath(),
    hasSubCategories: c.subCategories.size > 0,
  }))
}
